/**
 * Tabs — the single user-facing tab pattern (underline | pill).
 *
 * Consolidates the bespoke tab systems of the user area. Keyboard support is
 * the standard tablist behaviour (arrows + Home/End); the active tab is stated
 * by border/background AND by aria-selected, never by colour alone.
 *
 * URL synchronisation is deliberately NOT here: it belongs to Phase 3.
 * The Admin Console's own tab bar is untouched.
 */
import { useRef } from "react";

export function Tabs({
  items,               // [{ id, label, icon: Icon, badge, disabled, testId }]
  value,
  onChange,
  variant = "underline",
  ariaLabel = "Sezioni",
  className = "",
  testId = "tabs",
}) {
  const refs = useRef({});

  const onKeyDown = (e) => {
    const enabled = items.filter((i) => !i.disabled);
    const idx = enabled.findIndex((i) => i.id === value);
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = enabled[(idx + 1) % enabled.length];
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = enabled[(idx - 1 + enabled.length) % enabled.length];
    else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    if (next) {
      e.preventDefault();
      onChange(next.id);
      refs.current[next.id]?.focus();
    }
  };

  const isPill = variant === "pill";
  // When nothing in this tablist is selected the first enabled tab keeps the
  // tab stop: a tablist must never become unreachable from the keyboard.
  const hasSelection = items.some((i) => i.id === value && !i.disabled);
  const fallbackStopId = hasSelection ? null : items.find((i) => !i.disabled)?.id;

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
      onKeyDown={onKeyDown}
      className={`flex flex-wrap items-center ${isPill
        ? "gap-1 p-1 rounded-lg border border-line-soft bg-surface-subtle"
        : "gap-1 border-b border-line-soft"} ${className}`}
    >
      {items.map((item) => {
        const active = item.id === value;
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            ref={(el) => { refs.current[item.id] = el; }}
            type="button"
            role="tab"
            id={`tab-${item.id}`}
            aria-selected={active}
            aria-controls={`panel-${item.id}`}
            tabIndex={active || item.id === fallbackStopId ? 0 : -1}
            disabled={item.disabled}
            data-testid={item.testId || `tab-${item.id}`}
            onClick={() => onChange(item.id)}
            className={`inline-flex items-center gap-2 min-h-10 px-3 text-sm font-medium whitespace-nowrap
                        transition-colors duration-base ease-system
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-canvas
                        disabled:opacity-50 disabled:pointer-events-none
                        ${isPill
                          ? `rounded-md ${active
                            ? "bg-surface text-content shadow-card"
                            : "text-content-muted hover:text-content hover:bg-surface"}`
                          : `-mb-px border-b-2 ${active
                            ? "border-brand text-content"
                            : "border-transparent text-content-muted hover:text-content hover:border-line-strong"}`}`}
          >
            {Icon && <Icon className="w-4 h-4" aria-hidden="true" />}
            {item.label}
            {item.badge !== undefined && item.badge !== null && (
              <span className="ml-0.5 px-1.5 rounded-full bg-surface-subtle text-label text-content-secondary tabular">
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, active, className = "", children }) {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={`panel-${id}`}
      aria-labelledby={`tab-${id}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}

export default Tabs;
